import {
  MAP_DOMINANT_STATUS_ORDER,
  PRODUCT_HOLDING_STATUS_LABELS,
  type MapHoldingDto,
  type NeighbourCellDto,
  type ProductHoldingStatus,
} from '@nova/shared';
import { geoConicConformal, geoPath } from 'd3-geo';
import type { FeatureCollection, Geometry } from 'geojson';
import { useMemo, useState } from 'react';
import { cn } from '../../lib/cn.js';
import { EMPTY_FILL, MAP_STROKE, SELECTED_STROKE, STATUS_FILL } from './mapColors.js';
import { kommuneCode, useKommuner, type KommuneProps } from './geo/useKommuner.js';

const W = 300;
const H = 240;

/** Dominant status (for colouring) across a set of holdings — mirrors the API. */
export function dominantStatusOf(holdings: MapHoldingDto[]): ProductHoldingStatus | null {
  let best: ProductHoldingStatus | null = null;
  let bestRank = Infinity;
  for (const h of holdings) {
    const rank = MAP_DOMINANT_STATUS_ORDER.indexOf(h.status);
    if (rank < bestRank) {
      bestRank = rank;
      best = h.status;
    }
  }
  return best;
}

/**
 * Local map of a kommune and its bordering kommuner, for reference selling /
 * clusters. The focus kommune is outlined in accent; neighbours are coloured by
 * the dominant status of their matching holdings. Clicking a neighbour with an
 * organisation opens it.
 */
export function KommuneNeighboursMap({
  centerCode,
  centerName,
  centerStatus,
  neighbours,
  onOpenNeighbour,
}: {
  centerCode: string;
  centerName: string;
  centerStatus: ProductHoldingStatus | null;
  neighbours: NeighbourCellDto[];
  onOpenNeighbour: (organizationId: string) => void;
}) {
  const { data: features } = useKommuner();
  const [hover, setHover] = useState<string | null>(null);

  const statusByCode = useMemo(() => {
    const m = new Map<string, ProductHoldingStatus | null>();
    for (const n of neighbours) m.set(n.kommunenummer, n.status);
    return m;
  }, [neighbours]);

  const paths = useMemo(() => {
    if (!features) return [];
    const codes = new Set([centerCode, ...neighbours.map((n) => n.kommunenummer)]);
    const subset = features.filter((f) => codes.has(kommuneCode(f)));
    if (subset.length === 0) return [];
    const collection: FeatureCollection<Geometry, KommuneProps> = {
      type: 'FeatureCollection',
      features: subset,
    };
    const projection = geoConicConformal()
      .rotate([-10.4, 0])
      .parallels([58, 72])
      .fitExtent(
        [
          [8, 8],
          [W - 8, H - 8],
        ],
        collection,
      );
    const path = geoPath(projection);
    return subset.map((f) => ({ code: kommuneCode(f), name: f.properties.name, d: path(f) ?? '' }));
  }, [features, neighbours, centerCode]);

  const orgByCode = useMemo(() => {
    const m = new Map<string, NeighbourCellDto>();
    for (const n of neighbours) m.set(n.kommunenummer, n);
    return m;
  }, [neighbours]);

  if (!features || paths.length === 0) {
    return <p className="text-ink-soft text-xs">Kartutsnitt ikke tilgjengelig.</p>;
  }

  function fillFor(code: string): string {
    if (code === centerCode) return centerStatus ? STATUS_FILL[centerStatus] : 'rgba(56,189,248,0.18)';
    const status = statusByCode.get(code);
    return status ? STATUS_FILL[status] : EMPTY_FILL;
  }

  const hoverNeighbour = hover ? orgByCode.get(hover) : null;

  return (
    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="border-hairline bg-base/20 relative rounded-lg border p-1">
        <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label={`Nabokommuner til ${centerName}`}>
          {paths.map((p) => {
            const isCenter = p.code === centerCode;
            const neighbour = orgByCode.get(p.code);
            const clickable = !isCenter && neighbour?.organizationId;
            return (
              <path
                key={p.code}
                d={p.d}
                fill={fillFor(p.code)}
                stroke={isCenter ? SELECTED_STROKE : MAP_STROKE}
                strokeWidth={isCenter ? 1.6 : 0.5}
                className={cn(clickable && 'cursor-pointer')}
                onMouseEnter={() => setHover(p.code)}
                onMouseLeave={() => setHover((h) => (h === p.code ? null : h))}
                onClick={() => neighbour?.organizationId && onOpenNeighbour(neighbour.organizationId)}
              />
            );
          })}
        </svg>
        {hoverNeighbour && (
          <div className="border-hairline bg-panel pointer-events-none absolute bottom-1 left-1 right-1 rounded-md border px-2 py-1 text-[11px]">
            <span className="text-ink font-medium">{hoverNeighbour.name ?? `Kommune ${hoverNeighbour.kommunenummer}`}</span>
            {hoverNeighbour.holdings.length > 0 ? (
              <span className="text-ink-soft">
                {' '}
                · {hoverNeighbour.holdings.map((h) => h.productName).join(', ')}
              </span>
            ) : (
              <span className="text-ink-soft/70"> · ingen i utvalget</span>
            )}
          </div>
        )}
      </div>

      <ul className="space-y-1.5 overflow-y-auto sm:max-h-[240px]">
        {neighbours.length === 0 && <li className="text-ink-soft text-xs">Ingen nabokommuner i kartet.</li>}
        {neighbours.map((n) => (
          <li key={n.kommunenummer}>
            <button
              type="button"
              disabled={!n.organizationId}
              onClick={() => n.organizationId && onOpenNeighbour(n.organizationId)}
              className={cn(
                'border-hairline bg-base/20 flex w-full items-start justify-between gap-2 rounded-md border px-2.5 py-1.5 text-left',
                n.organizationId ? 'hover:bg-raised/40 cursor-pointer' : 'opacity-70',
              )}
            >
              <span className="min-w-0">
                <span className="text-ink flex items-center gap-1.5 text-xs font-medium">
                  <span
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ background: n.status ? STATUS_FILL[n.status] : EMPTY_FILL }}
                    aria-hidden
                  />
                  {n.name ?? `Kommune ${n.kommunenummer}`}
                </span>
                {n.holdings.length > 0 ? (
                  <span className="text-ink-soft/80 mt-0.5 block text-[11px]">
                    {n.holdings
                      .map((h) => `${h.productName}: ${PRODUCT_HOLDING_STATUS_LABELS[h.status]}${h.competitor ? ` (${h.competitor})` : ''}`)
                      .join(' · ')}
                  </span>
                ) : (
                  <span className="text-ink-soft/50 mt-0.5 block text-[11px]">
                    {n.organizationId ? 'Ingen beholdning i utvalget' : 'Ikke i registeret'}
                  </span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
